import { useState, useEffect } from "react";
import { Briefcase, Route, GraduationCap, Package, Plus, X, Loader2, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const TYPE_CFG = {
  service: { icon: Briefcase, label: "Service", color: "#3b82f6", bg: "#3b82f610" },
  accompagnement: { icon: Route, label: "Accompagnement", color: "#8b5cf6", bg: "#8b5cf610" },
  formation: { icon: GraduationCap, label: "Formation", color: "#10b981", bg: "#10b98110" },
  offre_tenant: { icon: Package, label: "Offre Tenant", color: "#f7c935", bg: "#f7c93510" },
};

export default function CatalogSelector({ api, selected = [], onChange }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTab, setPickerTab] = useState("service");
  const [search, setSearch] = useState("");

  const loadCatalog = async () => {
    if (catalog) { setShowPicker(true); return; }
    setLoading(true);
    try {
      const r = await api.get("/facturation/catalog");
      const items = r.data || [];
      // Group by catalog_type
      const grouped = {};
      for (const item of items) {
        const t = item.catalog_type || "service";
        if (!grouped[t]) grouped[t] = [];
        grouped[t].push(item);
      }
      setCatalog(grouped);
      setShowPicker(true);
    } catch (e) {
      // Fallback to old endpoint
      try {
        const r = await api.get("/automation/catalog-items");
        setCatalog({ service: r.data?.services || [], accompagnement: r.data?.accompagnements || [], formation: r.data?.formations || [] });
        setShowPicker(true);
      } catch (e2) { console.error(e2); }
    }
    setLoading(false);
  };

  const addItem = (item) => {
    const id = item.catalog_id || item.template_id || item.formation_id;
    if (selected.some(s => (s.catalog_id || s.template_id || s.formation_id) === id)) return;
    onChange([...selected, { ...item, type: item.catalog_type || item.type || "service" }]);
  };

  const removeItem = (idx) => onChange(selected.filter((_, i) => i !== idx));

  const getItems = () => {
    if (!catalog) return [];
    const items = catalog[pickerTab] || [];
    if (search) return items.filter(i => (i.name || "").toLowerCase().includes(search.toLowerCase()));
    return items;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs text-[#888194] font-bold">Catalogue (services, accompagnements, offres)</label>
        <button onClick={loadCatalog} disabled={loading}
          className="text-xs text-[#f7c935] hover:underline flex items-center gap-1"
          data-testid="add-catalog-item-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Ajouter du catalogue
        </button>
      </div>

      {/* Selected items */}
      {selected.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {selected.map((item, idx) => {
            const cfg = TYPE_CFG[item.type || item.catalog_type] || TYPE_CFG.service;
            const Icon = cfg.icon;
            return (
              <div key={idx} className="flex items-center gap-2 p-2 rounded-lg border border-white/[0.06]" style={{ background: cfg.bg }}
                data-testid={`catalog-selected-${idx}`}>
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: cfg.color }} />
                <span className="text-xs font-medium flex-1">{item.name}</span>
                {item.price && <span className="text-xs text-[#888194]">{item.price} EUR</span>}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                <button onClick={() => removeItem(idx)} className="p-0.5 hover:bg-red-500/10 rounded"><X className="w-3 h-3 text-red-400" /></button>
              </div>
            );
          })}
        </div>
      )}

      {/* Picker overlay */}
      {showPicker && catalog && (
        <div className="bg-[#0b0811] border border-white/[0.08] rounded-xl p-4 space-y-3 mb-3" data-testid="catalog-picker">
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(TYPE_CFG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const count = (catalog[key] || []).length;
              if (count === 0) return null;
              return (
                <button key={key} onClick={() => { setPickerTab(key); setSearch(""); }}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${pickerTab === key ? "bg-[#f7c935]/10 text-[#f7c935]" : "text-[#888194] hover:text-white"}`}
                  data-testid={`picker-tab-${key}`}>
                  <Icon className="w-3 h-3" /> {cfg.label}s ({count})
                </button>
              );
            })}
            <div className="flex-1" />
            <button onClick={() => setShowPicker(false)} className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4 text-[#888194]" /></button>
          </div>

          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." className="bg-[#14101c] border-white/10 text-white text-xs h-8" data-testid="catalog-search" />

          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {getItems().length === 0 ? (
              <p className="text-xs text-[#888194] py-4 text-center">Aucun element dans cette categorie</p>
            ) : getItems().map((item, i) => {
              const id = item.catalog_id;
              const alreadySelected = selected.some(s => s.catalog_id === id);
              return (
                <button key={i} onClick={() => !alreadySelected && addItem(item)} disabled={alreadySelected}
                  className={`w-full flex items-center gap-2 p-2.5 rounded-lg text-left text-xs transition-colors ${alreadySelected ? "opacity-40 cursor-not-allowed" : "hover:bg-white/[0.04]"} border border-white/[0.04]`}
                  data-testid={`catalog-item-${id}`}>
                  <span className="font-medium flex-1 text-white">{item.name}</span>
                  {item.price && <span className="text-[#888194]">{item.price} EUR</span>}
                  {item.category && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-[#888194]">{item.category}</span>}
                  {!alreadySelected && <Plus className="w-3 h-3 text-[#f7c935]" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
